import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "../i18n/config";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Detectar si estamos en desarrollo local
const isDevelopment = false //process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Usar backend local en desarrollo, servidor remoto en producción
export const BACKEND_URL = isDevelopment 
  ? (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000")  // para uso local
  : "https://api-sentinelpro.autosignsweb.com"; // para subir al server

export const API_URL = `${BACKEND_URL}/api`; 

export const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get locale code based on current language
export const getLocale = () => {
  const lang = i18n.language || "es";
  const localeMap = {
    es: "es-ES",
    en: "en-US",
    fr: "fr-FR",
  };
  return localeMap[lang] || "es-ES";
};

export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleTimeString(getLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const feetToMeters = (feet) => feet * 0.3048;
export const metersToFeet = (meters) => meters / 0.3048;

export const speakMessage = (message) => {
  if ("speechSynthesis" in window) {
    const utterance = new SpeechSynthesisUtterance(message);
    const lang = i18n.language || "es";
    const langMap = {
      es: "es-ES",
      en: "en-US",
      fr: "fr-FR",
    };
    utterance.lang = langMap[lang] || "es-ES";
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    window.speechSynthesis.speak(utterance);
  }
};

// These will be replaced by translation functions in components
// Keeping for backward compatibility, but components should use translations
export const getManualCheckReasons = (t) => [
  { value: "gps_failure", label: t("manualCheckReasons.gps_failure") },
  { value: "signal_interference", label: t("manualCheckReasons.signal_interference") },
  { value: "device_malfunction", label: t("manualCheckReasons.device_malfunction") },
  { value: "building_obstruction", label: t("manualCheckReasons.building_obstruction") },
  { value: "weather_conditions", label: t("manualCheckReasons.weather_conditions") },
  { value: "other", label: t("manualCheckReasons.other") },
];

export const getUserRoles = (t) => [
  { value: "super_admin", label: t("roles.super_admin") },
  { value: "admin", label: t("roles.admin") },
  { value: "revendedor", label: t("roles.revendedor") },
  { value: "supervisor", label: t("roles.supervisor") },
  { value: "operador", label: t("roles.operador") },
  { value: "vigilante", label: t("roles.vigilante") },
];

// Roles del sistema (los demás son roles personalizados creados por la compañía)
export const BUILTIN_ROLES = ["super_admin", "admin", "revendedor", "supervisor", "operador", "vigilante"];

// Permisos propios de la interfaz de patrulla. Quien solo tenga estos no entra al panel.
export const GUARD_PERMISSIONS = ["patrol.perform", "visitors.use"];

// Roles a los que se les puede asignar un horario semanal de trabajo.
export const SCHEDULE_ROLES = ["vigilante", "supervisor"];
export const roleUsesSchedule = (role) => SCHEDULE_ROLES.includes(role);
// Solo los vigilantes están obligados a tener horario; para el resto es opcional.
export const roleRequiresSchedule = (role) => role === "vigilante";

export const userHasPermission = (user, perm) =>
  user?.role === "super_admin" || (user?.permissions || []).includes(perm);

export const userHasPanelAccess = (user) =>
  user?.role === "super_admin" ||
  (user?.permissions || []).some((perm) => !GUARD_PERMISSIONS.includes(perm));

export const userCanPatrol = (user) => userHasPermission(user, "patrol.perform");

// Etiqueta de un rol: traducción para los del sistema, nombre guardado para los personalizados
export const getRoleLabel = (t, roleKey, roleName) => {
  if (BUILTIN_ROLES.includes(roleKey)) return t(`roles.${roleKey}`);
  return roleName || roleKey || "";
};

export const getPaymentTypes = (t) => [
  { value: "hourly", label: t("paymentTypes.hourly") },
  { value: "mixed", label: t("paymentTypes.mixed") },
];

export const getRouteOrders = (t) => [
  { value: "sequential", label: t("routeOrders.sequential") },
  { value: "random", label: t("routeOrders.random") },
];

// Legacy exports for backward compatibility (will use Spanish as fallback)
export const MANUAL_CHECK_REASONS = [
  { value: "gps_failure", label: "Falla de GPS" },
  { value: "signal_interference", label: "Interferencia de señal" },
  { value: "device_malfunction", label: "Mal funcionamiento del dispositivo" },
  { value: "building_obstruction", label: "Obstrucción por edificio" },
  { value: "weather_conditions", label: "Condiciones climáticas" },
  { value: "other", label: "Otro" },
];

export const USER_ROLES = [
  { value: "super_admin", label: "Super Administrador" },
  { value: "admin", label: "Administrador" },
  { value: "revendedor", label: "Revendedor" },
  { value: "supervisor", label: "Supervisor" },
  { value: "operador", label: "Operador" },
  { value: "vigilante", label: "Vigilante" },
];

export const PAYMENT_TYPES = [
  { value: "hourly", label: "Por hora" },
  { value: "mixed", label: "Mixto (Fijo + Horas)" },
];

export const ROUTE_ORDERS = [
  { value: "sequential", label: "Secuencial" },
  { value: "random", label: "Aleatorio" },
];

// Function to translate API error messages
// Maps common Spanish API error messages to translation keys
export const translateApiMessage = (message, t) => {
  if (!message || typeof message !== "string") {
    return message;
  }

  const messageLower = message.toLowerCase().trim();
  
  // Map of Spanish API messages to translation keys
  const messageMap = {
    "credenciales inválidas": t("api.errors.invalidCredentials"),
    "usuario desactivado": t("api.errors.userDeactivated"),
    "error al guardar": t("api.errors.saveError"),
    "error al eliminar": t("api.errors.deleteError"),
    "error al cargar": t("api.errors.loadError"),
    "error al cargar datos": t("api.errors.loadError"),
    "error al iniciar sesión": t("api.errors.loginError"),
    "no autorizado": t("api.errors.unauthorized"),
    "acceso denegado": t("api.errors.forbidden"),
    "recurso no encontrado": t("api.errors.notFound"),
    "error del servidor": t("api.errors.serverError"),
    "error de conexión": t("api.errors.networkError"),
    "guardado correctamente": t("api.success.saved"),
    "eliminado correctamente": t("api.success.deleted"),
    "actualizado correctamente": t("api.success.updated"),
    "creado correctamente": t("api.success.created"),
  };

  // Try exact match first
  if (messageMap[messageLower]) {
    return messageMap[messageLower];
  }

  // Try partial matches for common patterns
  if (messageLower.includes("credenciales")) {
    return t("api.errors.invalidCredentials");
  }
  if (messageLower.includes("desactivado")) {
    return t("api.errors.userDeactivated");
  }
  if (messageLower.includes("error al guardar") || messageLower.includes("error guardando")) {
    return t("api.errors.saveError");
  }
  if (messageLower.includes("error al eliminar") || messageLower.includes("error eliminando")) {
    return t("api.errors.deleteError");
  }
  if (messageLower.includes("error al cargar") || messageLower.includes("error cargando")) {
    return t("api.errors.loadError");
  }
  if (messageLower.includes("no autorizado") || messageLower.includes("unauthorized")) {
    return t("api.errors.unauthorized");
  }
  if (messageLower.includes("acceso denegado") || messageLower.includes("forbidden")) {
    return t("api.errors.forbidden");
  }
  if (messageLower.includes("no encontrado") || messageLower.includes("not found")) {
    return t("api.errors.notFound");
  }

  // If no match found, return original message
  return message;
};

// Helper function to get API error message with translation
export const getApiErrorMessage = (error, t, fallbackKey = "api.errors.unknownError") => {
  if (!error) {
    return t(fallbackKey);
  }

  // Try to get message from error response
  const apiMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
  
  if (apiMessage) {
    return translateApiMessage(apiMessage, t);
  }

  // Fallback to generic error
  return t(fallbackKey);
};

// Format phone number to (999) 999-9999 format
export const formatPhoneNumber = (value) => {
  if (!value) return "";
  
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, "");
  
  // Limit to 10 digits
  const phoneNumberDigits = phoneNumber.slice(0, 10);
  
  // Format based on length
  if (phoneNumberDigits.length === 0) return "";
  if (phoneNumberDigits.length <= 3) return `(${phoneNumberDigits}`;
  if (phoneNumberDigits.length <= 6) {
    return `(${phoneNumberDigits.slice(0, 3)}) ${phoneNumberDigits.slice(3)}`;
  }
  return `(${phoneNumberDigits.slice(0, 3)}) ${phoneNumberDigits.slice(3, 6)}-${phoneNumberDigits.slice(6)}`;
};

// Format Dominican ID (cédula) to 000-0000000-0
export const formatCedula = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10)}`;
};

// Remove formatting from phone number (returns only digits)
export const unformatPhoneNumber = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, "");
};
